// Doctor specialties and sub-specialties — one catalogue shared by Profile and
// Onboarding (each used to keep its own copy). Add a row here and both screens
// pick it up.
//
// The Persian string is the VALUE, and it stays Persian in every language: it is
// what `doctor.specialty` already holds on thousands of rows, so translating it
// would either orphan those rows or need a data migration. Only the label the
// doctor reads is translated, via `SPECIALTY_LABEL_KEYS`.
//
// Two rules govern edits:
//
//   1. NEVER change or remove an existing Persian value. A stored row that no
//      longer matches a catalogue entry falls back to rendering verbatim, but it
//      also disappears from the picker — the doctor would have to re-pick, and
//      `specialty` is what decides which app they are routed into.
//   2. Order in this file is irrelevant. The picker sorts alphabetically at
//      render time, in the CURRENT UI language (see `specialtyOptions`), so a
//      new row can be appended to the group it belongs to without re-sorting.
//
// The kind — تخصص vs فوق‌تخصص — is what the picker groups by. Iran's board
// certifications separate the two, and a doctor looking for «نفرولوژی» expects
// to find it under فوق‌تخصص rather than mixed into a flat list of eighty.

export const SPECIALTY = 'specialty';
export const SUBSPECIALTY = 'subspecialty';

// [slug, storedPersianValue, kind]
//
// The slug is stable and ASCII so the catalogue stays greppable, and it names the
// i18n key: `common.specialty.<slug>`. Every slug here MUST exist in fa/en/ar.
//
// i18n-exempt: stored identifiers and clinical vocabulary, not UI copy — the
// label a doctor reads is translated at the render site via `specialtyLabel`.
const CATALOGUE = [
  // ── تخصص ──────────────────────────────────────────────────────────────────
  ['general',                'عمومی',                      SPECIALTY],
  ['internal',               'داخلی',                      SPECIALTY],
  ['cardiology',             'قلب و عروق',                 SPECIALTY],
  ['neurology',              'مغز و اعصاب',                SPECIALTY],
  ['orthopedics',            'ارتوپدی',                    SPECIALTY],
  ['obgyn',                  'زنان و زایمان',              SPECIALTY],
  ['pediatrics',             'اطفال',                      SPECIALTY],
  ['generalSurgery',         'جراحی عمومی',                SPECIALTY],
  ['ophthalmology',          'چشم',                        SPECIALTY],
  ['ent',                    'گوش و حلق و بینی',           SPECIALTY],
  ['dermatology',            'پوست',                       SPECIALTY],
  ['psychiatry',             'روانپزشکی',                  SPECIALTY],
  ['urology',                'اورولوژی',                   SPECIALTY],
  ['dentistry',              'دندانپزشکی',                 SPECIALTY],
  ['physiotherapy',          'فیزیوتراپی',                 SPECIALTY],
  ['radiology',              'رادیولوژی',                  SPECIALTY],
  ['anesthesiology',         'بیهوشی',                     SPECIALTY],
  ['pathology',              'آسیب‌شناسی',                 SPECIALTY],
  ['forensicMedicine',       'پزشکی قانونی',               SPECIALTY],
  ['nuclearMedicine',        'پزشکی هسته‌ای',              SPECIALTY],
  ['sportsMedicine',         'پزشکی ورزشی',                SPECIALTY],
  ['familyMedicine',         'پزشکی خانواده',              SPECIALTY],
  ['socialMedicine',         'پزشکی اجتماعی',              SPECIALTY],
  ['emergencyMedicine',      'طب اورژانس',                 SPECIALTY],
  ['occupationalMedicine',   'طب کار',                     SPECIALTY],
  ['preventiveMedicine',     'طب پیشگیری',                 SPECIALTY],
  ['physicalMedicine',       'طب فیزیکی و توانبخشی',       SPECIALTY],
  ['traditionalMedicine',    'طب سنتی',                    SPECIALTY],
  ['neurosurgery',           'جراحی مغز و اعصاب',          SPECIALTY],
  ['radiationOncology',      'رادیوتراپی',                 SPECIALTY],
  ['medicalGenetics',        'ژنتیک پزشکی',                SPECIALTY],
  ['infectiousDiseases',     'بیماری‌های عفونی',           SPECIALTY],
  ['psychology',             'روان‌شناسی',                 SPECIALTY],
  ['nutrition',              'تغذیه',                      SPECIALTY],

  // ── فوق‌تخصص ──────────────────────────────────────────────────────────────
  ['endocrinology',          'غدد',                        SUBSPECIALTY],
  ['pulmonology',            'ریه',                        SUBSPECIALTY],
  ['gastroenterology',       'گوارش',                      SUBSPECIALTY],
  ['hematology',             'خون‌شناسی',                  SUBSPECIALTY],
  ['oncology',               'انکولوژی',                   SUBSPECIALTY],
  ['nephrology',             'نفرولوژی',                   SUBSPECIALTY],
  ['rheumatology',           'روماتولوژی',                 SUBSPECIALTY],
  ['allergyImmunology',      'آسم و آلرژی',                SUBSPECIALTY],
  ['criticalCare',           'مراقبت‌های ویژه',            SUBSPECIALTY],
  ['neonatology',            'نوزادان',                    SUBSPECIALTY],
  ['pediatricCardiology',    'قلب کودکان',                 SUBSPECIALTY],
  ['pediatricNeurology',     'اعصاب کودکان',               SUBSPECIALTY],
  ['pediatricNephrology',    'نفرولوژی کودکان',            SUBSPECIALTY],
  ['pediatricGastroenterology', 'گوارش کودکان',            SUBSPECIALTY],
  ['pediatricEndocrinology', 'غدد کودکان',                 SUBSPECIALTY],
  ['pediatricHematology',    'خون و انکولوژی کودکان',      SUBSPECIALTY],
  ['pediatricSurgery',       'جراحی کودکان',               SUBSPECIALTY],
  ['cardiacSurgery',         'جراحی قلب و عروق',           SUBSPECIALTY],
  ['thoracicSurgery',        'جراحی توراکس',               SUBSPECIALTY],
  ['vascularSurgery',        'جراحی عروق',                 SUBSPECIALTY],
  ['plasticSurgery',         'جراحی پلاستیک و ترمیمی',     SUBSPECIALTY],
  ['colorectalSurgery',      'جراحی کولورکتال',            SUBSPECIALTY],
  ['interventionalCardiology', 'قلب مداخله‌ای',            SUBSPECIALTY],
  ['electrophysiology',      'الکتروفیزیولوژی قلب',        SUBSPECIALTY],
  ['geriatrics',             'طب سالمندی',                 SUBSPECIALTY],
  ['perinatology',           'پریناتولوژی',                SUBSPECIALTY],
  ['gynecologicOncology',    'انکولوژی زنان',              SUBSPECIALTY],
  ['infertility',            'نازایی و ناباروری',          SUBSPECIALTY],
  ['palliativeCare',         'طب تسکینی',                  SUBSPECIALTY],
  ['painMedicine',           'طب درد',                     SUBSPECIALTY],
];

// ── Persian collation ─────────────────────────────────────────────────────────
//
// `localeCompare(a, b, 'fa')` needs an ICU build that carries the Persian
// collation, and jsdom/node without full-icu silently falls back to code-point
// order — which puts «آ» after «ی» and reads as random to a Persian speaker. The
// alphabet is 32 letters; spelling it out is cheaper than the dependency.
//
// i18n-exempt: a collation table, not UI copy.
const FA_ALPHABET = 'آابپتثجچحخدذرزژسشصضطظعغفقکگلمنوهی';

const FA_RANK = Object.fromEntries(
  [...FA_ALPHABET].map((ch, i) => [ch, i]),
);

// Fold the shapes that mean the same letter, and drop what carries no order:
// ZWNJ (which is inside «خون‌شناسی» and would otherwise sort before every
// letter), spaces, and the Arabic hamza forms Persian text picks up from
// copy-paste.
// i18n-exempt: a normalisation table, not UI copy.
const FA_FOLD = { 'ي': 'ی', 'ك': 'ک', 'ة': 'ه', 'أ': 'ا', 'إ': 'ا', 'ؤ': 'و', 'ئ': 'ی', 'ى': 'ی' };

const faKey = (s) => [...String(s || '')]
  .map((ch) => FA_FOLD[ch] || ch)
  .filter((ch) => ch !== '‌' && ch !== ' ')
  .join('');

/** Persian dictionary order. Anything the alphabet does not know sorts last. */
export const compareFa = (a, b) => {
  const x = faKey(a);
  const y = faKey(b);
  const n = Math.min(x.length, y.length);
  for (let i = 0; i < n; i += 1) {
    const rx = FA_RANK[x[i]] ?? Number.MAX_SAFE_INTEGER;
    const ry = FA_RANK[y[i]] ?? Number.MAX_SAFE_INTEGER;
    if (rx !== ry) return rx - ry;
    if (rx === Number.MAX_SAFE_INTEGER && x[i] !== y[i]) return x[i] < y[i] ? -1 : 1;
  }
  return x.length - y.length;
};

// ── Derived lookups ───────────────────────────────────────────────────────────

/**
 * Every stored value, in Persian alphabetical order.
 *
 * The order here is what a Persian UI shows; `specialtyOptions` re-sorts by the
 * translated label when the UI is not Persian.
 */
export const SPECIALTIES = CATALOGUE
  .map(([, value]) => value)
  .sort(compareFa);

export const SPECIALTY_LABEL_KEYS = Object.fromEntries(
  CATALOGUE.map(([slug, value]) => [value, `common.specialty.${slug}`]),
);

export const SPECIALTY_KINDS = Object.fromEntries(
  CATALOGUE.map(([, value, kind]) => [value, kind]),
);

export const SPECIALTY_GROUP_KEYS = {
  [SPECIALTY]: 'common.specialtyGroup.specialty',
  [SUBSPECIALTY]: 'common.specialtyGroup.subspecialty',
};

/**
 * The label for a stored specialty. A value the catalogue has never heard of —
 * an older row, or one typed by hand — is shown verbatim rather than dropped.
 */
export const specialtyLabel = (t, value) => {
  const key = SPECIALTY_LABEL_KEYS[value];
  return key ? t(key) : (value || '');
};

/**
 * Picker options: `{ value, label, group, groupLabel }`, تخصص before فوق‌تخصص and
 * alphabetical inside each group.
 *
 * Alphabetical in WHICH alphabet depends on the UI language: Persian sorts by
 * the stored value with `compareFa`, English and Arabic by the translated label
 * with `localeCompare` — sorting an English list by its Persian value would look
 * shuffled, and vice versa.
 *
 * `extra` carries a value the catalogue does not know (a legacy row), so editing
 * a profile never silently blanks a specialty it cannot name.
 */
export const specialtyOptions = (t, locale = 'fa', extra = '') => {
  const options = CATALOGUE.map(([slug, value, kind]) => ({
    value,
    label: t(`common.specialty.${slug}`),
    group: kind,
    groupLabel: t(SPECIALTY_GROUP_KEYS[kind]),
  }));

  if (extra && !SPECIALTY_LABEL_KEYS[extra]) {
    options.push({
      value: extra,
      label: extra,
      group: SPECIALTY,
      groupLabel: t(SPECIALTY_GROUP_KEYS[SPECIALTY]),
    });
  }

  const byLabel = locale === 'fa'
    ? (a, b) => compareFa(a.value, b.value)
    : (a, b) => a.label.localeCompare(b.label, locale);

  return options.sort((a, b) => {
    if (a.group !== b.group) return a.group === SPECIALTY ? -1 : 1;
    return byLabel(a, b);
  });
};
